import {
  Center,
  Combobox,
  ComboboxItem,
  Loader,
  MultiSelectProps,
  Pill,
  PillsInput,
  useCombobox,
} from "@mantine/core"
import { useTranslation } from "react-i18next"
import { ScrollContainer } from "./scroll-container"

export interface DefaultSelectProps
  extends Omit<MultiSelectProps, "renderOption"> {
  options: ComboboxItem[]
  loading?: boolean
  multiple?: boolean
  onSelectionChange?: (selectedValues: string[]) => void
  onSearchChange?: (searchValue: string) => void
  renderOption?: (item: ComboboxItem) => JSX.Element
  onScrolledToBottom?: () => void
}

export function DefaultSelect({
  options,
  multiple,
  loading,
  searchValue,
  onSelectionChange,
  onScrolledToBottom,
  onSearchChange,
  renderOption,
  label,
  value,
  placeholder,
  ...props
}: DefaultSelectProps) {
  const { t } = useTranslation("app_server")
  const [internalSearchValue, setinternalSearchValue] = React.useState(
    searchValue ?? ""
  )

  React.useEffect(
    () => onSearchChange && onSearchChange(internalSearchValue),
    [internalSearchValue]
  )

  React.useEffect(() => {
    setinternalSearchValue(searchValue ?? "")
  }, [searchValue])

  const [selectedValues, setSelectedValues] = React.useState<string[]>(
    value ?? []
  )

  React.useEffect(() => {
    onSelectionChange && onSelectionChange(selectedValues)
  }, [selectedValues])

  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  })

  const handleValueSelect = (val: string) => {
    setSelectedValues((current) =>
      multiple
        ? current.includes(val)
          ? current.filter((v) => v !== val)
          : [...current, val]
        : [val]
    )
    if (!multiple) {
      combobox.closeDropdown()
      setinternalSearchValue("")
    }
  }

  const handleValueRemove = (val: string) =>
    setSelectedValues((current) => current.filter((v) => v !== val))

  const viewportRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    const scrollElement = viewportRef.current
    if (!scrollElement) return

    const handleScroll = () => {
      if (
        scrollElement.scrollHeight -
          scrollElement.scrollTop -
          scrollElement.clientHeight <
          10 &&
        onScrolledToBottom
      ) {
        onScrolledToBottom()
      }
    }

    scrollElement.addEventListener("scroll", handleScroll)
    return () => scrollElement.removeEventListener("scroll", handleScroll)
  }, [onScrolledToBottom])

  return (
    <Combobox onOptionSubmit={handleValueSelect} store={combobox}>
      <Combobox.Target>
        <PillsInput
          disabled={props.disabled}
          error={props.error}
          label={label}
          withAsterisk={!!props.required}
          onClick={() => combobox.openDropdown()}
        >
          <Pill.Group>
            {selectedValues.map((selectedValue) => {
              const option = options.find(
                (option) => option.value === selectedValue
              )
              if (option?.value) {
                return (
                  <Pill
                    key={option.value}
                    withRemoveButton
                    onRemove={() => handleValueRemove(option.value)}
                  >
                    {option.label}
                  </Pill>
                )
              } else {
                // happens if data is not loaded/paginated
                return (
                  <Pill
                    key={selectedValue}
                    withRemoveButton
                    onRemove={() => handleValueRemove(selectedValue)}
                  >
                    {selectedValue}
                  </Pill>
                )
              }
            })}

            {(multiple || selectedValues.length === 0) && (
              <Combobox.EventsTarget>
                <PillsInput.Field
                  onFocus={() => combobox.openDropdown()}
                  onBlur={() => combobox.closeDropdown()}
                  value={internalSearchValue}
                  placeholder={placeholder ?? t("Search")}
                  onChange={(event) => {
                    combobox.updateSelectedOptionIndex()
                    setinternalSearchValue(event.currentTarget.value)
                  }}
                  onKeyDown={(event) => {
                    if (
                      event.key === "Backspace" &&
                      internalSearchValue.length === 0 &&
                      selectedValues.length > 0
                    ) {
                      event.preventDefault()
                      handleValueRemove(
                        selectedValues[selectedValues.length - 1]
                      )
                    }
                  }}
                />
              </Combobox.EventsTarget>
            )}
          </Pill.Group>
        </PillsInput>
      </Combobox.Target>

      <Combobox.Dropdown>
        <Combobox.Options>
          <ScrollContainer mah={300}>
            <div ref={viewportRef}>
              {!loading && options.length === 0 ? (
                <Combobox.Empty>{t("NothingFound")}</Combobox.Empty>
              ) : (
                options.map((option) => {
                  return renderOption ? (
                    renderOption(option)
                  ) : (
                    <Combobox.Option {...option} key={option.value}>
                      {option.label}
                    </Combobox.Option>
                  )
                })
              )}
              {loading && (
                <Center style={{ padding: 8 }}>
                  <Loader size="xs" />
                </Center>
              )}
            </div>
          </ScrollContainer>
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  )
}
